import { Link } from 'react-router';

import { PipelineAttentionBadge } from '@/features/pipeline/PipelineAttentionBadge';
import { TodayRailAnnouncer, TodayRailBadge } from '@/features/today/TodayRailCount';
import { useTodayCount } from '@/features/today/useTodayCount';

import { NavigationIcon } from './NavigationIcon';
import type { NavDestination } from './navigation';

export interface RailItemProps {
  readonly destination: NavDestination;
  /** The page the link opens for this person. */
  readonly path: string;
  /** Whether the person is inside this destination. */
  readonly current: boolean;
}

interface RailLinkProps extends RailItemProps {
  /** Whether a count rides on the link, which puts it in the link's name. */
  readonly counted?: boolean;
  /** The count, drawn after the label. */
  readonly children?: React.ReactNode;
}

function labelIdOf(destination: NavDestination): string {
  return `nav-label-${destination.id}`;
}

function badgeIdOf(destination: NavDestination): string {
  return `nav-badge-${destination.id}`;
}

function RailLink({
  destination,
  path,
  current,
  counted = false,
  children,
}: RailLinkProps): React.JSX.Element {
  const labelId = labelIdOf(destination);
  return (
    // Named by every word it shows (WCAG 2.5.3): the label, then the count riding on Sales or
    // Today. axe compares the name with the words painted on screen, and aria-hidden does not
    // take a painted word out of that comparison.
    <Link
      to={path}
      className="fo-rail__link"
      data-testid={`nav-${destination.id}`}
      aria-current={current ? 'page' : undefined}
      aria-labelledby={counted ? `${labelId} ${badgeIdOf(destination)}` : labelId}
    >
      <NavigationIcon id={destination.id} />
      {/* The words name the link. The rail clips them rather than hides them when it collapses,
          so the name is the same whether or not they are painted, and the caption painted in
          their place is decoration, not a second copy of the name. */}
      <span className="fo-rail__label" id={labelId}>
        {destination.label}
      </span>
      <span className="fo-rail__caption" aria-hidden="true">
        <span>{destination.label}</span>
      </span>
      {children}
    </Link>
  );
}

function SalesRailItem(props: RailItemProps): React.JSX.Element {
  return (
    <li>
      {/* The space inside keeps the label and the count two words apart in the painted text; the
          rail lays them out, so it adds no gap of its own. */}
      <RailLink {...props} counted>
        {' '}
        <span id={badgeIdOf(props.destination)}>
          <PipelineAttentionBadge />
        </span>
      </RailLink>
    </li>
  );
}

function TodayRailItem(props: RailItemProps): React.JSX.Element {
  const total = useTodayCount();
  return (
    <li>
      <RailLink {...props} counted>
        {' '}
        <TodayRailBadge id={badgeIdOf(props.destination)} total={total} />
      </RailLink>
      {/* Outside the link, so a change is announced on its own and not as part of the link. */}
      <TodayRailAnnouncer total={total} />
    </li>
  );
}

/**
 * One destination in the rail.
 *
 * Sales carries the count of deals that need attention and Today the count of everything waiting;
 * every other destination is its label and glyph alone.
 *
 * @param props see {@link RailItemProps}
 * @returns the list item
 */
export function RailItem(props: RailItemProps): React.JSX.Element {
  if (props.destination.id === 'today') {
    return <TodayRailItem {...props} />;
  }
  if (props.destination.id === 'sales') {
    return <SalesRailItem {...props} />;
  }
  return (
    <li>
      <RailLink {...props} />
    </li>
  );
}
